import React, { ReactNode, useMemo } from 'react';

import { BeakerIcon, RefreshIcon } from '@heroicons/react/outline';
import {
    ArrowsExpandIcon,
    CodeIcon,
    DocumentTextIcon,
    ExternalLinkIcon,
    MinusCircleIcon,
    PresentationChartBarIcon,
} from '@heroicons/react/outline';
import { DownloadIcon } from '@heroicons/react/solid';

import DownloadDataMoreMenuItem from '@components/DownloadDataMoreMenuItem';
import { LoadingIndicator } from '@components/LoadingButton';
import { Item } from '@components/MoreMenuIconButton';

import { useOptionalExperimentDetailViewContext } from '@contexts/ExperimentDetailViewContext';

import useAuth from '@hooks/useAuth';
import useClonePlot from '@hooks/useClonePlot';
import useComments from '@hooks/useComments';
import useExperimentPermissions from '@hooks/useExperimentPermissions';
import useOrganizationPermissions from '@hooks/useOrganizationPermissions';

import { CheckCircleIcon } from '@icons/custom/CheckCircleIcon';
import CommentsIcon from '@icons/custom/CommentsIcon';
import { CopyIcon } from '@icons/custom/CopyIcon';
import { EditIcon } from '@icons/custom/EditIcon';

import { ImageAnalysis } from '@models/analysis/ImageAnalysis';
import { PrismAnalysis } from '@models/analysis/PrismAnalysis';
import { SpreadsheetAnalysis } from '@models/analysis/SpreadsheetAnalysis';
import Experiment from '@models/Experiment';
import { isArrowPlotData } from '@models/ExperimentData';
import { AnyExperimentData, PlotPipelinePlotData } from '@models/ExperimentData';
import Plot from '@models/Plot';
import { PlotDisplayOption } from '@models/PlotDisplayOption';
import { isServerImageDisplayType } from '@models/PlotDisplayType';
import { isSponsor } from '@models/User';

import Endpoints from '@services/Endpoints';
import { appendQueryParams, QueryParam } from '@services/QueryParams';

import { generateReportFileName } from '@util/ExperimentUtil';
import { updateHashWithPlotUUID } from '@util/hashUtil';
import { hasCommentsPermission } from '@util/PermissionUtil';

type Props = {
    disableDownloadLinks?: boolean;
    experiment?: Experiment | null;
    refreshThumbnail?: () => void;
    hideEditButton?: boolean;
    hideEditForm?: boolean;
    plot?: Plot;
    plotData?: AnyExperimentData | null;
    publicKey?: string | null;
    ready: boolean;
    setFullScreen?: (full: boolean) => void;
    setMethodsOpen: (open: boolean) => void;
    setResultsOpen: (open: boolean) => void;
    showExperimentLink?: boolean;
};
const usePlotMenuItems = ({
    disableDownloadLinks,
    experiment,
    refreshThumbnail,
    hideEditButton,
    hideEditForm,
    plot,
    plotData,
    publicKey,
    ready,
    setFullScreen,
    setMethodsOpen,
    setResultsOpen,
    showExperimentLink,
}: Props) => {
    const plotId = plot?.uuid || '';
    const {
        archivePlot,
        getPlotOverrides,
        handleChangeSelectedPlot,
        setCommentsModalOpen,
        setCurrentPlotPanel,
        setEvidenceModalOpen,
        setEvidencePlotId,
        setExperimentModalOpen,
        setHideEditForm,
        setOpenOnComments,
        setPlotToEmbed,
        setPresentationMode,
    } = useOptionalExperimentDetailViewContext();
    const { clonePlot, cloning, cloneDisabled } = useClonePlot({ publicKey, experiment });
    const overrides = getPlotOverrides?.(plotId) ?? { hasOverrides: false };
    const permissions = useExperimentPermissions(experiment, !!publicKey);
    const hasItems = isArrowPlotData(plotData) ? (plotData.count ?? 0) > 0 : (plotData?.items ?? []).length > 0;
    const { commentCount } = useComments({ object_type: 'plot', object_uuid: plotId });
    const { features } = useOrganizationPermissions();
    const isIframe = typeof window !== 'undefined' && window.self !== window.top;
    const { user } = useAuth();
    const isSponsorUser = isSponsor(user);
    const disableEdit = !permissions.canEdit || hideEditButton;

    const openEditModalOnComments = () => {
        if (!plot) return;
        handleChangeSelectedPlot?.(plot);
        setOpenOnComments?.(true);
        setCurrentPlotPanel?.(plot?.analysis?.uuid ? 'plot' : 'analysis');
        if (hideEditForm) {
            setHideEditForm?.(true);
        }
        setExperimentModalOpen?.(true);
    };

    const openCommentsModal = () => {
        if (!plot) return;
        handleChangeSelectedPlot?.(plot);
        setCommentsModalOpen?.(true);
    };

    const plotCanBeExported = true;

    const menuItems = useMemo<(Item | ReactNode)[]>(() => {
        const menuItems: (Item | ReactNode)[] = [];
        if (!disableEdit) {
            menuItems.push({
                label: plot?.analysis?.uuid ? 'Edit' : 'Configure',
                onClick: () => {
                    if (!plot) return;
                    handleChangeSelectedPlot?.(plot);
                    setOpenOnComments?.(false);
                    setCurrentPlotPanel?.(plot?.analysis?.uuid ? 'plot' : 'analysis');
                    if (hideEditForm) {
                        setHideEditForm?.(true);
                    }
                    setExperimentModalOpen?.(true);
                    updateHashWithPlotUUID(plotId);
                },
                icon: <EditIcon className="h-4 w-4" />,
                closeOnClick: true,
            });
        }

        if (!disableEdit && permissions.canEdit && !cloneDisabled) {
            menuItems.push({
                label: 'Clone',
                onClick: async () => {
                    await clonePlot({
                        linkedExperimentId: plot?.linked_experiment_id,
                    });
                },
                icon: cloning ? <LoadingIndicator size={16} /> : <CopyIcon width={16} height={16} />,
                closeOnClick: false,
            });
        }

        // Plot download links
        if (ready && !disableDownloadLinks) {
            if (plotCanBeExported) {
                menuItems.push({
                    label: 'Export',
                    href: appendQueryParams(
                        `/experiments/${experiment?.pluto_id}/plots/${plotId}/export`,
                        overrides?.hasOverrides
                            ? {
                                  [QueryParam.ANALYSIS_ID]: overrides.analysis_id,
                                  [QueryParam.DISPLAY_ID]: overrides.display_id,
                              }
                            : undefined,
                    ),
                    icon: <DownloadIcon className="h-4 w-4" />,
                    newTab: true,
                    closeOnClick: true,
                });
            } else if ((plot?.analysis as ImageAnalysis)?.image_url) {
                const analysisId = overrides.analysis_id ?? plot?.analysis?.uuid;
                menuItems.push(
                    <DownloadDataMoreMenuItem
                        key={'download_image'}
                        endpoint={
                            !analysisId || !experiment?.uuid
                                ? undefined
                                : () => {
                                      return Endpoints.lab.experiment.analysis.imageAnalysisSignedUrl(
                                          {
                                              experimentId: experiment.uuid,
                                              analysisId,
                                          },
                                          { download: true },
                                      );
                                  }
                        }
                        url={analysisId ? undefined : (plot?.analysis as ImageAnalysis).image_url}
                        baseFilename={generateReportFileName({ experiment, suffix: 'plot' })}
                        extension=""
                        label="Download"
                    />,
                );
            } else if ((plot?.analysis as SpreadsheetAnalysis)?.spreadsheet_url) {
                const analysisId = overrides.analysis_id ?? plot?.analysis?.uuid;
                menuItems.push(
                    <DownloadDataMoreMenuItem
                        key={'download_image'}
                        endpoint={
                            !analysisId || !experiment?.uuid
                                ? undefined
                                : () => {
                                      return Endpoints.lab.experiment.analysis.spreadsheetAnalysisSignedUrl(
                                          {
                                              experimentId: experiment.uuid,
                                              analysisId,
                                          },
                                          { download: true },
                                      );
                                  }
                        }
                        url={analysisId ? undefined : (plot?.analysis as SpreadsheetAnalysis).spreadsheet_url}
                        baseFilename={generateReportFileName({ experiment, suffix: 'plot' })}
                        extension=""
                        label="Download"
                    />,
                );
            } else if (isServerImageDisplayType(plot?.display.display_type)) {
                // const analysisId = overrides.analysis_id ?? plot?.analysis?.uuid;
                const pdfs = (plotData as PlotPipelinePlotData | null)?.plots?.images?.pdf;
                const pngs = (plotData as PlotPipelinePlotData | null)?.plots?.images?.png;
                const pdfUrl = (plot.display as PlotDisplayOption)?.is_full_width
                    ? pdfs?.wide
                    : (pdfs?.square ?? pdfs?.auto);
                const pngUrl = (plot.display as PlotDisplayOption)?.is_full_width
                    ? pngs?.wide
                    : (pngs?.square ?? pngs?.auto);

                if (setFullScreen) {
                    menuItems.push({
                        label: 'Full-screen',
                        onClick: async () => {
                            await setFullScreen(true);
                        },
                        icon: <ArrowsExpandIcon width={16} />,
                        closeOnClick: false,
                    });
                }

                menuItems.push(
                    <DownloadDataMoreMenuItem
                        key={'download_pdf'}
                        url={pdfUrl}
                        baseFilename={generateReportFileName({ experiment, suffix: 'plot' })}
                        extension=".pdf"
                        label="Download .pdf"
                        newTab
                    />,
                    <DownloadDataMoreMenuItem
                        key={'download_png'}
                        url={pngUrl}
                        baseFilename={generateReportFileName({ experiment, suffix: 'plot' })}
                        extension=".png"
                        label="Download .png"
                        newTab
                    />,
                );
            }
        }

        if (showExperimentLink) {
            menuItems.push({
                label: 'Open original experiment',
                href: `/experiments/${plot?.source_experiment_id || experiment?.pluto_id}/analysis#plot-${plotId}`,
                closeOnClick: false,
                newTab: true,
                icon: <ExternalLinkIcon className="h-4 w-4" />,
            });
        }

        if (!isSponsorUser && !!features?.experiment_features.biomarkers_enabled && !disableEdit) {
            menuItems.push({
                label: 'Save as evidence',
                onClick: () => {
                    setEvidenceModalOpen?.(true);
                    setEvidencePlotId?.(plotId);
                },
                icon: <CheckCircleIcon className="h-4 w-4" />,
                closeOnClick: true,
            });
        }

        if (!isSponsorUser && !hideEditButton) {
            menuItems.push({
                label: 'Embed',
                onClick: () => {
                    if (!plot) return;
                    setPlotToEmbed?.(plot);
                },
                icon: <CodeIcon className="h-4 w-4" />,
                closeOnClick: true,
            });
        }

        const commentsEnabled = hasCommentsPermission({ features, experiment, user });
        if (commentsEnabled && permissions.canEdit && !!handleChangeSelectedPlot) {
            menuItems.push({
                label: `Comments${commentCount ? ` (${commentCount})` : ''}`,
                onClick: () => {
                    if (!!publicKey || !!isIframe) return openCommentsModal();
                    openEditModalOnComments();
                },
                icon: <CommentsIcon className="h-4 w-4" />,
                closeOnClick: true,
            });
        }

        if (setPresentationMode && !publicKey && !isIframe) {
            menuItems.push({
                label: 'Present',
                onClick: () => {
                    setPresentationMode(true);
                },
                icon: <PresentationChartBarIcon className="h-4 w-4" />,
                closeOnClick: true,
            });
        }

        if (plot?.analysis) {
            menuItems.push({
                label: 'View methods',
                onClick: () => setMethodsOpen(true),
                icon: <DocumentTextIcon className="h-4 w-4" />,
                closeOnClick: true,
            });
        }

        if (plot?.analysis?.category.shortname !== 'content' && hasItems && !publicKey && !isSponsorUser) {
            menuItems.push({
                label: 'View results',
                onClick: () => setResultsOpen(true),
                icon: <BeakerIcon className="h-4 w-4" />,
                closeOnClick: true,
            });
        }

        if (plot?.analysis as PrismAnalysis) {
            if ((plot?.analysis as PrismAnalysis)?.prism_file?.signed_url) {
                menuItems.push({
                    label: 'Download Prism File',
                    href: (plot?.analysis as PrismAnalysis).prism_file.signed_url,
                    icon: <DownloadIcon className="h-4 w-4" />,
                    closeOnClick: true,
                    newTab: true,
                });
            }

            if ((plot?.analysis as PrismAnalysis)?.prism_project_link) {
                menuItems.push({
                    label: 'View in Prism Cloud',
                    href: (plot?.analysis as PrismAnalysis).prism_project_link || '',
                    closeOnClick: true,
                    newTab: true,
                    icon: <ExternalLinkIcon className="h-4 w-4" />,
                });
            }
        }

        if (plot?.thumbnail_uri?.rectangle) {
            menuItems.push({
                label: 'Refresh thumbnail',
                onClick: () => {
                    refreshThumbnail?.();
                },
                icon: <RefreshIcon className="h-4 w-4" />,
                closeOnClick: true,
            });
        }

        if (!hideEditButton && permissions.canEdit) {
            menuItems.push({
                label: 'Remove',
                onClick: () => {
                    archivePlot?.(plotId, false, plot?.linked_experiment_id);
                },
                icon: <MinusCircleIcon className="h-4 w-4" />,
                closeOnClick: true,
                color: 'danger',
            });
        }

        return menuItems;
    }, [ready, plot, permissions, overrides, hideEditButton, disableEdit, refreshThumbnail]);

    return { menuItems };
};

export default usePlotMenuItems;
